import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import useAnimate from '../ui/Motion';

import Reveal from '../ui/Reveal';
import WordReveal from '../ui/WordReveal';
import { faqs as defaultFaqs, contact } from '../data/site';

const EASE = [0.16, 1, 0.3, 1];

/**
 * An accordion rather than the old two-column wall of open answers.
 * Six questions and six answers at once is a page of text nobody reads;
 * the questions alone are a list somebody can scan.
 *
 * The answer is height-animated, so it is unmounted when closed. That
 * keeps it out of the accessibility tree and out of in-page find, which
 * is the honest behaviour for content that is genuinely not shown.
 */
export default function Faqs({ items = defaultFaqs, eyebrow = 'Answers' }) {
    const [open, setOpen] = useState(null);
    const reduced = !useAnimate();

    return (
        <section className="section section--ruled" id="faqs">
            {/* An accordion answer is unmounted while it is closed, which is
                the honest behaviour for content that genuinely is not shown -
                but it also means a crawler never reads a word of it. The same
                questions and answers go out as FAQPage data, which is what
                Google and every assistant actually parse them from. */}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: items.map(faq => ({
                            '@type': 'Question',
                            name: faq.question,
                            acceptedAnswer: { '@type': 'Answer', text: faq.answer }
                        }))
                    })}
                </script>
            </Helmet>

            <div className="shell faq">
                <div className="faq__aside">
                    <Reveal y={18}>
                        <span className="t-eyebrow t-eyebrow--accent">{eyebrow}</span>
                    </Reveal>

                    <WordReveal as="h2" className="t-h2" text="Frequently asked questions." />

                    <Reveal delay={0.12}>
                        <p className="t-body">
                            Still unsure about something? Call{' '}
                            <a className="link" href={contact.phoneHref}>{contact.phone}</a>{' '}
                            and talk it through with someone who will be on the job.
                        </p>
                    </Reveal>
                </div>

                <ul className="faq__list">
                    {items.map((faq, i) => {
                        const isOpen = open === i;

                        return (
                            <motion.li
                                className={`faq__item${isOpen ? ' faq__item--open' : ''}`}
                                key={faq.question}
                                initial={reduced ? false : { opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                            >
                                <h3>
                                    <button
                                        type="button"
                                        className="faq__q"
                                        aria-expanded={isOpen}
                                        aria-controls={`faq-a-${i}`}
                                        id={`faq-q-${i}`}
                                        onClick={() => setOpen(current => (current === i ? null : i))}
                                    >
                                        {faq.question}
                                        <span className="faq__sign" aria-hidden="true" />
                                    </button>
                                </h3>

                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            className="faq__a"
                                            id={`faq-a-${i}`}
                                            role="region"
                                            aria-labelledby={`faq-q-${i}`}
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.42, ease: EASE }}
                                        >
                                            <p>{faq.answer}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}
